<script setup lang="ts">
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { ref } from 'vue'
import RlEmptyDataset from '@/components/common/RlEmptyDataset.vue'
import TeamDialog from '@/components/core/teams/TeamDialog.vue'
import type { ITeam } from '@/types/core/Teams'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { useTeams } from '@/composables/core/teams'
import { useOIDCStore } from '@/stores/oidc.store'
import { phoneFormatter } from '@/helpers/ep/formatters'
import type { IRemoveTeamMemberForm, ITransferTeamOwnershipForm } from '@/types/core/forms'
import AddTeamMembersDialog from '@/components/core/teams/AddTeamMembersDialog.vue'
import TeamsFAQ from '@/components/core/teams/TeamsFAQ.vue'
import { ArrowDown } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const { profile } = useOIDCStore()
const {
  deleteTeamMutation,
  leaveTeamMutation,
  removeTeamMemberMutation,
  transferTeamOwnershipMutation,
  getUserTeamQuery,
  getUserTeamInvitationsQuery,
} = useTeams()

const upsertDialogVisible = ref<boolean>(false)
const addMembersDialogVisible = ref<boolean>(false)
const teamsFAQVisible = ref<boolean>(false)
const teamBeingModified = ref<ITeam>()

const {
  isFetching: isFetchingTeam,
  isError: isErrorTeam,
  isRefetching: isRefetchingTeam,
  isPending: isPendingTeam,
  data: teamData,
  error,
  refetch: refetchTeam,
} = getUserTeamQuery()
const { data: invitesData } = getUserTeamInvitationsQuery()

const handleTeamUpdate = (team: ITeam) => {
  teamBeingModified.value = team
  upsertDialogVisible.value = true
}

const handleDelete = (id: number) => {
  DeleteConfirmationModal(
    'Delete Team',
    'Are you sure you want to delete your team? This action cannot be undone.',
    id,
    deleteTeamMutation.mutateAsync
  )
}

const handleLeaveTeam = () => {
  DeleteConfirmationModal(
    'Leave Team',
    'Are you sure you want to leave this team?',
    undefined,
    leaveTeamMutation.mutateAsync,
    undefined,
    'Leave'
  )
}

const handleRemoveMember = (teamID: number, member: any) => {
  const form: IRemoveTeamMemberForm = {
    teamID: teamID,
    memberID: member.id,
  }
  DeleteConfirmationModal(
    'Remove Member',
    `Are you sure you want to remove ${member.name} from the team? You can always invite them again`,
    form,
    removeTeamMemberMutation.mutateAsync,
    undefined,
    'Remove'
  )
}

const handleOwnershipTransfer = (teamID: number, member: any) => {
  const form: ITransferTeamOwnershipForm = {
    teamID: teamID,
    memberID: member.id,
  }
  DeleteConfirmationModal(
    'Transfer Ownership',
    `Are you sure you want to transfer team ownership to ${member.name}? This action cannot be undone`,
    form,
    transferTeamOwnershipMutation.mutateAsync,
    undefined,
    'Transfer'
  )
}

const handleInvitesNavigation = () => {
  router.push({ name: 'team-invitations' })
}
</script>

<template>
  <ViewLayout v-if="teamData" :title="teamData.name">
    <template v-if="!isErrorTeam" #controls>
      <el-button type="primary" @click="teamsFAQVisible = true">FAQs</el-button>
      <el-dropdown v-if="teamData.owner.id == profile?.id">
        <el-button type="primary">
          Actions<el-icon class="arrow-icon">&nbsp;<ArrowDown /></el-icon>
        </el-button>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item :disabled="isFetchingTeam" @click="addMembersDialogVisible = true"
              >Invite</el-dropdown-item
            >
            <el-dropdown-item :disabled="isFetchingTeam" @click="handleTeamUpdate(teamData)"
              >Update team</el-dropdown-item
            >
            <el-dropdown-item :disabled="isFetchingTeam" @click="handleDelete(teamData.id)"
              >Delete team</el-dropdown-item
            >
          </el-dropdown-menu>
        </template>
      </el-dropdown>
      <el-button
        v-if="teamData.owner.id != profile?.id"
        type="danger"
        :disabled="isFetchingTeam"
        @click="handleLeaveTeam()"
        >Leave Team</el-button
      >
    </template>
    <rl-error-result v-if="isErrorTeam && !isRefetchingTeam" :error="error">
      <el-button @click="refetchTeam()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <el-table v-loading="isPendingTeam" :data="teamData.members" stripe>
        <el-table-column prop="name" label="Name" :min-width="136">
          <template #default="scope">
            <div class="flex items-center gap-4">
              <el-avatar class="shrink-0" :size="30">{{ scope.row.name[0] }}</el-avatar>
              <div class="shrink-0">{{ scope.row.name }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="email" label="Email" :min-width="136" />
        <el-table-column prop="phone" label="Phone Number" :formatter="phoneFormatter" />
        <el-table-column label="Owner">
          <template #default="scope">
            <Icon
              v-if="scope.row.id == teamData.owner.id"
              icon="material-symbols:shield-person-rounded"
              inline
              height="20"
            />
          </template>
        </el-table-column>
        <el-table-column>
          <template #default="scope">
            <div class="flex gap-1">
              <el-button
                v-if="teamData.owner.id == profile?.id && teamData.owner.id != scope.row.id"
                title="Delete"
                link
                @click="handleRemoveMember(teamData!.id, scope.row)"
              >
                <Icon icon="material-symbols:delete-outline-rounded" inline height="20" />
              </el-button>
              <el-button
                v-if="teamData.owner.id == profile?.id && teamData.owner.id != scope.row.id"
                title="Transfer Ownership"
                link
                @click="handleOwnershipTransfer(teamData!.id, scope.row)"
              >
                <Icon icon="material-symbols:admin-panel-settings-outline-rounded" inline height="20" />
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </template>
  </ViewLayout>
  <ViewLayout v-else title="Teams">
    <RlEmptyDataset title="You are not part of any team" subtitle="You can either create one or wait for an invitation">
      <div class="flex flex-col gap-4">
        <el-button type="primary" @click="upsertDialogVisible = true">Create team</el-button>
        <el-badge :value="invitesData?.length" :show-zero="false" class="item">
          <el-button type="primary" @click="handleInvitesNavigation">View invites</el-button>
        </el-badge>
      </div>
    </RlEmptyDataset>
  </ViewLayout>
  <TeamDialog
    v-model="upsertDialogVisible"
    :team-to-update="teamBeingModified"
    @closed="teamBeingModified = undefined"
  />
  <TeamsFAQ v-model="teamsFAQVisible" />
  <AddTeamMembersDialog v-if="teamData" v-model="addMembersDialogVisible" :team="teamData" />
</template>

<style scoped lang="scss">
.el-button {
  margin: 0;
}
</style>
